/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Input, Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { PasswordDialogComponent } from './password-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthenticationService, ConfirmDialogComponent, NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent } from '@alfresco/aca-shared';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AdminService } from '../admin/admin.service';
import { UserDialogComponent } from '../admin/user-dialog.component';
import { BulkItem, bulkReport, errorSummary, isConflict, runEach } from '../shared/bulk';
import { PromptDialogComponent } from '../shared/prompt-dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/** The administrators group, used to mark and count admin accounts. */
const ADMIN_GROUP = 'GROUP_ALFRESCO_ADMINISTRATORS';

/** The zone of a Share site's own groups, which are managed from the site rather than here. */
const SITE_ZONE = 'APP.SHARE';

interface UserRow {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  organization: string;
  enabled: boolean;
  isAdmin: boolean;
}

/**
 * Users (UI-11).
 *
 * Reads Alfresco's People API and offers the operations it actually supports:
 * create, edit, enable/disable and change password. Deleting a person is not
 * offered — the repository's own delete is a legacy hard delete that orphans the
 * content that person created; disabling the account is the reversible action
 * that matches what “ระงับผู้ใช้งาน” means in the reference.
 *
 * Two figures the reference shows have no equivalent here and are therefore not
 * shown: “ผู้ใช้งานภายนอก” (no external-identity flag on a person in this
 * deployment) and “รอการเชิญ” (Community has no invitation state). Last-login is
 * likewise absent from the People API, so that column is replaced by the account
 * details the API does return.
 */
@Component({
  selector: 'app-users-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './users-page.component.html',
  styleUrls: ['./users-page.component.scss']
})
export class UsersPageComponent implements OnInit {
  /**
   * Whether the screen is shown inside the settings page rather than on a
   * route of its own — it then drops its own title, because the page it
   * sits in already names it.
   */
  @Input() embedded = false;

  private readonly translate = inject(TranslateService);
  private readonly admin = inject(AdminService);
  private readonly notification = inject(NotificationService);
  private readonly dialog = inject(MatDialog);
  private readonly auth = inject(AuthenticationService);

  readonly users = signal<UserRow[]>([]);
  readonly loading = signal(true);
  readonly failed = signal('');
  readonly selected = signal<UserRow | null>(null);
  readonly selectedGroups = signal<{ id: string; displayName: string }[]>([]);
  readonly groupsLoading = signal(false);
  readonly saving = signal(false);

  term = '';
  status: 'all' | 'enabled' | 'disabled' = 'all';

  private readonly termSignal = signal('');
  private readonly statusSignal = signal<'all' | 'enabled' | 'disabled'>('all');

  /** Editable copy of the selected user, so cancelling leaves the row untouched. */
  edit = { firstName: '', lastName: '', email: '', jobTitle: '', organization: '' };

  readonly visibleUsers = computed(() => {
    const term = this.termSignal().trim().toLowerCase();
    const status = this.statusSignal();

    return this.users().filter((user) => {
      if (status === 'enabled' && !user.enabled) {
        return false;
      }
      if (status === 'disabled' && user.enabled) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [user.id, user.displayName, user.email, user.jobTitle, user.organization].some((field) => (field || '').toLowerCase().includes(term));
    });
  });

  /** Users ticked for a bulk action; only ever ones the filters show. */
  readonly checkedIds = signal<Set<string>>(new Set());
  readonly checkedCount = computed(() => this.checkedIds().size);
  readonly allChecked = computed(() => this.visibleUsers().length > 0 && this.visibleUsers().every((user) => this.checkedIds().has(user.id)));
  readonly someChecked = computed(() => this.checkedCount() > 0 && !this.allChecked());
  readonly working = signal(false);

  readonly totalCount = computed(() => this.users().length);
  readonly enabledCount = computed(() => this.users().filter((u) => u.enabled).length);
  readonly disabledCount = computed(() => this.users().filter((u) => !u.enabled).length);
  readonly adminCount = computed(() => this.users().filter((u) => u.isAdmin).length);

  ngOnInit(): void {
    this.reload();
  }

  // ---- data ---------------------------------------------------------------

  reload(): void {
    this.loading.set(true);
    this.failed.set('');

    forkJoin({
      people: this.admin.listPeople(0, 500),
      admins: this.admin.listGroupChildren(ADMIN_GROUP).pipe(catchError(() => of(null)))
    })
      .pipe(
        map(({ people, admins }) => {
          const adminIds = new Set<string>(
            (Array.isArray(admins) ? admins : (admins?.data ?? []))
              .filter((entry: any) => (entry.authorityType ?? entry.memberType) !== 'GROUP')
              .map((entry: any) => entry.shortName ?? entry.userName ?? entry.id)
              .filter(Boolean)
          );

          return (people?.list?.entries ?? []).map((e: any): UserRow => {
            const p = e.entry;
            return {
              id: p.id,
              displayName: p.displayName || [p.firstName, p.lastName].filter(Boolean).join(' ') || p.id,
              firstName: p.firstName ?? '',
              lastName: p.lastName ?? '',
              email: p.email ?? '',
              jobTitle: p.jobTitle ?? '',
              organization: p.company?.organization ?? '',
              enabled: p.enabled !== false,
              isAdmin: adminIds.has(p.id)
            };
          });
        })
      )
      .subscribe({
        next: (rows) => {
          this.users.set(rows);
          this.loading.set(false);
          this.keepVisibleChecks();

          const current = this.selected();
          if (current) {
            const fresh = rows.find((row) => row.id === current.id);
            this.selected.set(fresh ?? null);
          }
        },
        error: (err) => {
          this.loading.set(false);
          this.users.set([]);
          this.failed.set(this.describe(err, 'DMS.UI.D0A1BB'));
        }
      });
  }

  applyFilters(): void {
    this.termSignal.set(this.term);
    this.statusSignal.set(this.status);
    this.keepVisibleChecks();
  }

  clearFilters(): void {
    this.term = '';
    this.status = 'all';
    this.applyFilters();
  }

  // ---- selection ----------------------------------------------------------

  select(user: UserRow): void {
    if (this.selected()?.id === user.id) {
      this.selected.set(null);
      return;
    }

    this.selected.set(user);
    this.edit = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      jobTitle: user.jobTitle,
      organization: user.organization
    };

    this.loadGroups(user);
  }

  private loadGroups(user: UserRow): void {
    this.groupsLoading.set(true);
    this.selectedGroups.set([]);
    this.admin.listPersonGroups(user.id).subscribe({
      next: (res) => {
        this.selectedGroups.set((res?.list?.entries ?? []).map((e: any) => ({ id: e.entry.id, displayName: e.entry.displayName || e.entry.id })));
        this.groupsLoading.set(false);
      },
      error: () => {
        this.selectedGroups.set([]);
        this.groupsLoading.set(false);
      }
    });
  }

  closePanel(): void {
    this.selected.set(null);
  }

  // ---- actions ------------------------------------------------------------

  /**
   * Opens the create-user dialog. The dialog creates the person through the
   * People API and then adds them to any groups picked.
   */
  addUser(): void {
    this.dialog
      .open(UserDialogComponent, { width: '520px', restoreFocus: true })
      .afterClosed()
      .subscribe((created) => {
        if (created === true) {
          this.notification.showInfo(this.translate.instant('DMS.USERS_PAGE.USER_ADDED'));
          this.reload();
        }
      });
  }

  save(): void {
    const user = this.selected();
    if (!user || this.saving()) {
      return;
    }

    this.saving.set(true);
    this.admin
      .updatePerson(user.id, {
        firstName: this.edit.firstName,
        lastName: this.edit.lastName,
        email: this.edit.email,
        jobTitle: this.edit.jobTitle,
        company: { organization: this.edit.organization }
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.notification.showInfo(this.translate.instant('DMS.UI.26E600', { p0: user.displayName }));
          this.reload();
        },
        error: (err) => {
          this.saving.set(false);
          this.notification.showError(this.describe(err, 'DMS.UI.92DF7C'));
        }
      });
  }

  /** Enabling and disabling are the same reversible operation on `enabled`. */
  toggleEnabled(user: UserRow): void {
    const enable = !user.enabled;
    const verb = enable ? 'DMS.UI.098289' : 'DMS.UI.65B98F';
    const verbLabel = this.translate.instant(verb);

    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: this.translate.instant('DMS.UI.D02092', { p0: verbLabel }),
          message: this.translate.instant('DMS.UI.D07CC9', {
            p0: verbLabel,
            p1: user.displayName,
            p2: enable ? '' : this.translate.instant('DMS.UI.9A362D')
          }),
          yesLabel: verb,
          noLabel: 'DMS.UI.7D3094'
        },
        minWidth: '320px',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed !== true) {
          return;
        }
        this.admin.updatePerson(user.id, { enabled: enable }).subscribe({
          next: () => {
            this.notification.showInfo(this.translate.instant('DMS.UI.145D7A', { p0: verbLabel, p1: user.displayName }));
            this.reload();
          },
          error: (err) => this.notification.showError(this.describe(err, this.translate.instant('DMS.UI.5E3879', { p0: verbLabel })))
        });
      });
  }

  changePassword(user: UserRow): void {
    this.dialog
      .open(PasswordDialogComponent, { data: { displayName: user.displayName }, restoreFocus: true })
      .afterClosed()
      .subscribe((password?: string) => {
        if (!password) {
          return;
        }
        this.admin.updatePerson(user.id, { password }).subscribe({
          next: () => this.notification.showInfo(this.translate.instant('DMS.UI.03EE70', { p0: user.displayName })),
          error: (err) => this.notification.showError(this.describe(err, 'DMS.UI.DA6568'))
        });
      });
  }

  /** Exports what is on screen, so the file matches the filters the user applied — or just the ticked users. */
  exportCsv(rows: UserRow[] = this.visibleUsers()): void {
    const t = (key: string): string => this.translate.instant(key);
    const header = ['DMS.UI.8FA50C', 'DMS.UI.FE63FD', 'DMS.UI.6DF8FB', 'DMS.UI.0A2CD9', 'DMS.UI.81A5EB', 'DMS.UI.C9701A', 'DMS.UI.9E945E'].map(t);
    // a name or title that starts with = + - @ would run as a formula in Excel — kept as text
    const escape = (value: string) => {
      const text = value ?? '';
      const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
      return '"' + safe.replace(/"/g, '""') + '"';
    };

    const csv = [
      header.map(escape).join(','),
      ...rows.map((row) =>
        [
          row.id,
          row.displayName,
          row.email,
          row.jobTitle,
          row.organization,
          t(row.enabled ? 'DMS.UI.216768' : 'DMS.UI.65B98F'),
          t(row.isAdmin ? 'DMS.UI.2726B4' : 'DMS.UI.19CE3F')
        ]
          .map(escape)
          .join(',')
      )
    ].join('\r\n');

    // BOM so Excel opens the Thai text in the right encoding
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ---- ticking users for a bulk action ------------------------------------

  isChecked(user: UserRow): boolean {
    return this.checkedIds().has(user.id);
  }

  toggleCheck(user: UserRow): void {
    this.checkedIds.update((ids) => {
      const next = new Set(ids);
      if (!next.delete(user.id)) {
        next.add(user.id);
      }
      return next;
    });
  }

  toggleAllChecks(): void {
    this.checkedIds.set(this.allChecked() ? new Set() : new Set(this.visibleUsers().map((user) => user.id)));
  }

  clearChecks(): void {
    this.checkedIds.set(new Set());
  }

  private checkedUsers(): UserRow[] {
    const ids = this.checkedIds();
    return this.visibleUsers().filter((user) => ids.has(user.id));
  }

  /** A tick on a user the filters now hide would be acted on unseen. */
  private keepVisibleChecks(): void {
    const visible = new Set(this.visibleUsers().map((user) => user.id));
    this.checkedIds.update((ids) => new Set([...ids].filter((id) => visible.has(id))));
  }

  exportChecked(): void {
    this.exportCsv(this.checkedUsers());
  }

  /**
   * Enables or suspends the ticked accounts that are not so already. Your own
   * account is never suspended from here: that would lock you out of the
   * screen you would need to undo it.
   */
  setCheckedEnabled(enable: boolean): void {
    const me = (this.auth.getUsername() ?? '').toLowerCase();
    const checked = this.checkedUsers();
    const skipsSelf = !enable && checked.some((user) => user.id.toLowerCase() === me);
    const users = checked.filter((user) => user.enabled !== enable && (enable || user.id.toLowerCase() !== me));
    const action = enable ? 'DMS.BULK.ENABLE' : 'DMS.BULK.SUSPEND';

    if (!users.length) {
      this.notification.showInfo(this.translate.instant(enable ? 'DMS.BULK.ALREADY_ENABLED' : 'DMS.BULK.ALREADY_SUSPENDED'));
      return;
    }

    const message = [
      this.translate.instant(enable ? 'DMS.BULK.ENABLE_MESSAGE' : 'DMS.BULK.SUSPEND_MESSAGE', { count: users.length }),
      skipsSelf ? this.translate.instant('DMS.BULK.NOT_YOURSELF') : ''
    ]
      .filter(Boolean)
      .join(' ');

    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: enable ? 'DMS.BULK.ENABLE_TITLE' : 'DMS.BULK.SUSPEND_TITLE',
          message,
          yesLabel: action,
          noLabel: 'DMS.BULK.CANCEL'
        },
        minWidth: '320px',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed === true) {
          this.runBulk(users, (user) => this.admin.updatePerson(user.id, { enabled: enable }), enable ? 'DMS.BULK.ENABLED' : 'DMS.BULK.SUSPENDED');
        }
      });
  }

  /** Adds the ticked users to one group. Someone already in it counts as done. */
  addCheckedToGroup(): void {
    const users = this.checkedUsers();
    if (!users.length || this.working()) {
      return;
    }
    this.working.set(true);
    this.admin.listGroups(0, 500).subscribe({
      next: (res) => {
        this.working.set(false);
        const options = (res?.list?.entries ?? [])
          .map((e: any) => e.entry)
          .filter((group: any) => !(group.zones ?? []).includes(SITE_ZONE))
          .map((group: any) => ({ value: group.id as string, label: (group.displayName || group.id) as string }));
        if (!options.length) {
          this.notification.showInfo(this.translate.instant('DMS.BULK.NO_GROUPS'));
          return;
        }
        this.dialog
          .open(PromptDialogComponent, {
            data: {
              title: this.translate.instant('DMS.BULK.GROUP_TITLE', { count: users.length }),
              // no group is picked for them: adding people to the wrong group by pressing on is too easy
              fields: [{ key: 'group', label: 'DMS.BULK.GROUP', required: true, options, placeholder: 'DMS.BULK.CHOOSE_GROUP' }],
              confirm: 'DMS.BULK.ADD_TO_GROUP'
            },
            restoreFocus: true
          })
          .afterClosed()
          .subscribe((values?: Record<string, string>) => {
            const group = values?.['group'];
            if (group) {
              this.runBulk(
                users,
                (user) =>
                  this.admin.addGroupMember(group, user.id).pipe(catchError((error) => (isConflict(error) ? of(null) : throwError(() => error)))),
                'DMS.BULK.ADDED_TO_GROUP'
              );
            }
          });
      },
      error: (err) => {
        this.working.set(false);
        this.notification.showError(this.describe(err, 'DMS.BULK.UNKNOWN_ERROR'));
      }
    });
  }

  private runBulk(users: UserRow[], operation: (user: UserRow) => Observable<unknown>, action: string): void {
    if (!users.length || this.working()) {
      return;
    }
    this.working.set(true);
    runEach<UserRow & BulkItem>(
      users.map((user) => ({ ...user, name: user.displayName })),
      operation,
      (error) => errorSummary(error) || this.translate.instant('DMS.BULK.UNKNOWN_ERROR')
    ).subscribe((outcome) => {
      this.working.set(false);
      const report = bulkReport(outcome, this.translate.instant(action));
      const text = this.translate.instant(report.key, report.params);
      if (report.level === 'info') {
        this.notification.showInfo(text);
      } else if (report.level === 'warning') {
        this.notification.showWarning(text);
      } else {
        this.notification.showError(text);
      }
      this.clearChecks();
      this.reload();
      // the open panel's groups were read when it opened
      const open = this.selected();
      if (open && outcome.ok.includes(open.id)) {
        this.loadGroups(open);
      }
    });
  }

  // ---- view helpers -------------------------------------------------------

  initials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || (name?.[0] ?? '?').toUpperCase();
  }

  trackById(_: number, user: UserRow): string {
    return user.id;
  }

  trackByGroupId(_: number, group: { id: string }): string {
    return group.id;
  }

  private describe(error: any, fallback: string): string {
    return error?.error?.error?.briefSummary || error?.message || this.translate.instant(fallback);
  }
}
